/**
 * Enrolment as a pure state machine (UC-3, slices 04–05): editing → enrolling → uploading the
 * probe as the first face capture → saved, or failed at either call. One attempt carries its
 * ids from the first tap to the last retry: a retry resends the frozen request with the same
 * `enrolment_id`, `encounter_id` and `capture_id`, so a dropped connection never makes a
 * second subject, encounter or capture. Without a photo (text check, *Subjekty*) the attempt
 * ends after the enrol call. *Add photo to this subject* (SC-017) starts at the upload: its
 * subject exists already.
 */
import type {
  CaptureResponse,
  CaptureUpload,
  EncounterDto,
  EnrolSubjectRequest,
} from '@/services/api/humint'
import { type EnrolProblem, isEditable, isRetryable } from '../mappers/enrol-errors'

export interface EnrolIds {
  enrolmentId: string
  captureId: string
  /** The encounter the attempt opens: its idempotency key. */
  encounterId: string
}

/** What the capture upload carries beside the photo, frozen at the tap like the request. */
export interface CaptureExtras {
  location: CaptureUpload['location']
  /** *Add photo*: the new encounter the capture opens. */
  encounter: EncounterDto | null
  /** *Add photo* with *Same encounter*: the operator's current encounter. */
  sameEncounterRef: string | null
}

export const NO_CAPTURE_EXTRAS: CaptureExtras = {
  location: null,
  encounter: null,
  sameEncounterRef: null,
}

/** One enrolment attempt: its ids, when it started, and whether a photo goes with it. */
export interface EnrolAttempt {
  ids: EnrolIds
  /** The capture time sent with the photo, and the clock of the preview label. */
  startedAt: string
  withPhoto: boolean
}

/** The subject the enrol call created (or returned on a replay), or the one a photo is added to. */
export interface EnrolledSubject {
  subjectRef: string
  shortId: string
  displayName: string | null
  enrolledAt: string
  /** The encounter the enrolment opened (`enc-<uuid4>`); null when none was recorded. */
  encounterRef?: string | null
}

export type CaptureOutcome = CaptureResponse['status']

/** What the tap froze: the enrol call's body (null when the subject exists) and the capture's extras. */
interface Frozen {
  attempt: EnrolAttempt
  request: EnrolSubjectRequest | null
  extras: CaptureExtras
}

export type EnrolState =
  /** `problem`: the enrol call was refused as sent, nothing created; the form is open to fix it. */
  | { step: 'editing'; attempt: EnrolAttempt; problem?: EnrolProblem | null }
  | ({ step: 'enrolling'; request: EnrolSubjectRequest } & Omit<Frozen, 'request'>)
  | ({
      step: 'uploading'
      subject: EnrolledSubject
      sentBytes: number
      totalBytes: number
    } & Frozen)
  | ({
      step: 'saved'
      subject: EnrolledSubject
      /** Null without a photo. */
      capture: CaptureOutcome | null
      /** The encounter the enrolment or the photo opened or joined; null when unknown. */
      encounterRef: string | null
    } & Frozen)
  | ({
      step: 'failed'
      /** Null when the enrol call failed; set when only the photo is missing. */
      subject: EnrolledSubject | null
      problem: EnrolProblem
    } & Frozen)

export type EnrolAction =
  /** A fresh attempt: new ids, an empty form. */
  | { type: 'start'; attempt: EnrolAttempt }
  /**
   * The operator's tap: the request is frozen from here on. With `target` the photo goes to
   * that existing subject and no enrol call is made (`request` is then null).
   */
  | {
      type: 'submit'
      request: EnrolSubjectRequest | null
      extras?: CaptureExtras
      target?: EnrolledSubject | null
    }
  /** Send again what failed, with the same ids and content. */
  | { type: 'retry' }
  | { type: 'enrolled'; subject: EnrolledSubject }
  | { type: 'progress'; sentBytes: number; totalBytes: number }
  | { type: 'captured'; capture: CaptureOutcome; encounterRef?: string | null }
  | { type: 'failed'; problem: EnrolProblem }

export function initialEnrolState(attempt: EnrolAttempt): EnrolState {
  return { step: 'editing', attempt }
}

/** A request is on the wire: nothing else may start and the sheet stays open. */
export function isEnrolBusy(state: EnrolState): boolean {
  return state.step === 'enrolling' || state.step === 'uploading'
}

/** The subject exists on the server (the photo may still be missing). */
export function enrolledSubject(state: EnrolState): EnrolledSubject | null {
  return 'subject' in state ? state.subject : null
}

/** The photo is only in memory: "Neuložené" until the capture upload answered 200/201. */
export function isPhotoUnsaved(state: EnrolState): boolean {
  return state.attempt.withPhoto && !(state.step === 'saved' && state.capture !== null)
}

export function canRetry(state: EnrolState): boolean {
  return state.step === 'failed' && isRetryable(state.problem)
}

const frozen = ({ attempt, request, extras }: Frozen): Frozen => ({ attempt, request, extras })

const uploading = (from: Frozen, subject: EnrolledSubject): EnrolState => ({
  step: 'uploading',
  ...frozen(from),
  subject,
  sentBytes: 0,
  totalBytes: 0,
})

function retried(state: Extract<EnrolState, { step: 'failed' }>): EnrolState {
  const { attempt, request, extras, subject } = state
  if (subject) return uploading(state, subject)
  return request ? { step: 'enrolling', attempt, request, extras } : state
}

function submitted(
  attempt: EnrolAttempt,
  action: Extract<EnrolAction, { type: 'submit' }>
): EnrolState {
  const extras = action.extras ?? NO_CAPTURE_EXTRAS
  if (action.target) return uploading({ attempt, request: null, extras }, action.target)
  if (!action.request) return { step: 'editing', attempt }
  return { step: 'enrolling', attempt, request: action.request, extras }
}

/**
 * What the capture upload carries beside the photo. An enrolment's photo joins the encounter
 * its enrol call opened (`same_encounter_ref`): the server stores a capture that names no
 * encounter without one. *Add photo* sends what the tap froze.
 */
export function uploadExtras(
  from: Pick<Frozen, 'request' | 'extras'>,
  subject: EnrolledSubject
): CaptureExtras {
  if (!from.request) return from.extras
  return { ...from.extras, encounter: null, sameEncounterRef: subject.encounterRef ?? null }
}

/** The encounter a saved attempt ends in: the capture's answer, else what was sent or opened. */
function savedEncounterRef(from: Frozen, subject: EnrolledSubject, answered?: string | null) {
  return answered ?? from.extras.sameEncounterRef ?? subject.encounterRef ?? null
}

function afterEnrol(
  state: Extract<EnrolState, { step: 'enrolling' }>,
  subject: EnrolledSubject
): EnrolState {
  if (state.attempt.withPhoto) return uploading(state, subject)
  const encounterRef = savedEncounterRef(state, subject)
  return { step: 'saved', ...frozen(state), subject, capture: null, encounterRef }
}

function failed(state: EnrolState, problem: EnrolProblem): EnrolState {
  if (state.step !== 'enrolling' && state.step !== 'uploading') return state
  // Refused before the subject existed: back to the form, same ids.
  if (state.step === 'enrolling' && isEditable(problem)) {
    return { step: 'editing', attempt: state.attempt, problem }
  }
  const subject = state.step === 'uploading' ? state.subject : null
  return { step: 'failed', ...frozen(state), subject, problem }
}

export function enrolReducer(state: EnrolState, action: EnrolAction): EnrolState {
  switch (action.type) {
    case 'start':
      return initialEnrolState(action.attempt)
    case 'submit':
      return state.step === 'editing' ? submitted(state.attempt, action) : state
    case 'retry':
      return state.step === 'failed' && canRetry(state) ? retried(state) : state
    case 'enrolled':
      return state.step === 'enrolling' ? afterEnrol(state, action.subject) : state
    case 'progress':
      return state.step === 'uploading'
        ? {
            ...state,
            sentBytes: Math.min(action.sentBytes, action.totalBytes),
            totalBytes: action.totalBytes,
          }
        : state
    case 'captured': {
      if (state.step !== 'uploading') return state
      const { subject } = state
      const encounterRef = savedEncounterRef(state, subject, action.encounterRef)
      return { step: 'saved', ...frozen(state), subject, capture: action.capture, encounterRef }
    }
    case 'failed':
      return failed(state, action.problem)
  }
}
